import type { RoomType } from '../../../gql/graphql';
import { formatPrice } from '../../../common/utilities/priceFormatter';
import { type FC, useEffect, useState } from 'react';
import { IconBed, IconUser, IconMinus, IconPlus } from '@tabler/icons-react';
import { calculateAdjustedPrice } from '../../../common/utilities/priceAdjustment';
import { getRoomOriginalImageUrl } from '../../../common/utilities/cdnImage';
import CdnImage from '../../../components/CdnImage';
import type { RoomDetail } from '../../../hooks/useCreateBooking';

interface Props {
  room: RoomType;
  currency: string;
  tripSelected: boolean;
  setShowRoomAmenities: (room: RoomType) => void;
  currentSettedDivers: number;
  setCurrentSettedDivers: (divers: number) => void;
  selectedRoomIds: RoomDetail[];
  setSelectedRoomIds: (roomIds: RoomDetail[]) => void;
}

const RoomCard: FC<Props> = ({
  room,
  currency,
  tripSelected,
  setShowRoomAmenities,
  currentSettedDivers,
  setCurrentSettedDivers,
  selectedRoomIds,
  setSelectedRoomIds,
}) => {
  const [guestCount, setGuestCount] = useState(0);

  useEffect(() => {
    if (!tripSelected) {
      setGuestCount(0);
      setCurrentSettedDivers(0);
    }
  }, [setCurrentSettedDivers, tripSelected]);

  const handleGuestCount = (
    newCount: number,
    roomId: number,
    occupancy: number,
  ) => {
    if (newCount < 0) {
      return;
    }

    const maxGuestsAllowed = room.totalAvailable * occupancy;
    const totalSettedDivers = currentSettedDivers - guestCount + newCount;
    if (newCount > maxGuestsAllowed) {
      // Handle the error - perhaps show a message to the user
      return;
    }

    setGuestCount(newCount);
    setCurrentSettedDivers(totalSettedDivers);

    // Update selectedRoomIds based on newCount
    const updatedRoomIds = [...selectedRoomIds];
    const roomIndex = updatedRoomIds.findIndex(r => r.typeId === roomId);

    if (newCount > 0) {
      if (roomIndex !== -1) {
        updatedRoomIds[roomIndex] = {
          ...updatedRoomIds[roomIndex],
          numberOfRooms: newCount,
        };
      } else {
        updatedRoomIds.push({
          typeId: roomId,
          numberOfRooms: newCount,
          pricePerRoom: calculateAdjustedPrice(room.price),
        });
      }
    } else if (roomIndex !== -1) {
      updatedRoomIds.splice(roomIndex, 1);
    }

    setSelectedRoomIds(updatedRoomIds);
  };

  const photoUrl = getRoomOriginalImageUrl(room.roomPhotos);

  return (
    <>
      <div className="flex flex-col items-start gap-6 self-stretch mt-4 md:flex-row">
        <div className="w-full shrink-0 overflow-hidden rounded-2xl md:w-[16.5rem]">
          {photoUrl ? (
            <CdnImage
              className="block h-52 w-full object-cover md:h-[16.25rem]"
              src={photoUrl}
              variant="ROOMGRID"
              alt={`${room.typeName} - ${room.bedTypeName}`}
            />
          ) : (
            <div
              className="h-52 w-full bg-neutral-50 md:h-[16.25rem]"
              aria-hidden="true"
            />
          )}
        </div>
        <div className="flex flex-col items-start gap-4 self-stretch">
          <span className="text-xl font-semibold text-neutral-800">
            {room.typeName}
          </span>
          <div className="flex flex-col items-start gap-3 self-stretch text-neutral-500">
            <div className="flex w-36 min-w-[9rem] items-center gap-2">
              <IconBed size={20} />
              <span className="text-base font-normal leading-normal">
                {room.bedTypeName}
              </span>
            </div>
            <div className="flex w-36 min-w-[9rem] items-center gap-2">
              <IconUser size={20} />
              <span className="text-base font-normal leading-normal">
                {room.occupancy} guests/room
              </span>
            </div>
            <div className="flex w-36 min-w-[9rem] items-center gap-2">
              <IconBed size={20} />
              <span className="text-base font-normal leading-normal">
                {room.deckLevelName}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="text-base font-semibold leading-normal underline"
            onClick={() => setShowRoomAmenities(room)}
          >
            See all amenities
          </button>
          {tripSelected && (
            <div className="hidden md:flex items-center justify-between gap-6 self-stretch mt-6">
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-semibold leading-normal text-neutral-800">
                  {formatPrice(room.price, { currencySymbol: currency })}
                </span>
                <span className="text-sm font-normal text-neutral-500">
                  per guest/trip
                </span>
              </div>
              <div className="flex flex-col justify-center items-end gap-2">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="flex p-1 items-center gap-2 rounded-full"
                    onClick={() =>
                      handleGuestCount(
                        guestCount - 1,
                        room.typeId,
                        room.occupancy ?? 1,
                      )
                    }
                  >
                    <IconMinus size={20} />
                  </button>
                  <span className="text-center text-sm font-semibold">
                    {guestCount} guest
                  </span>
                  <button
                    type="button"
                    className="flex p-1 items-center gap-2 rounded-full"
                    onClick={() =>
                      handleGuestCount(
                        guestCount + 1,
                        room.typeId,
                        room.occupancy ?? 1,
                      )
                    }
                  >
                    <IconPlus size={20} />
                  </button>
                </div>
                <div className="flex px-3 py-1 justify-center items-start gap-2 self-stretch rounded-full bg-red-100">
                  <span className="text-xs font-semibold text-status-error">
                    {room.totalAvailable * (room.occupancy ?? 1)} spaces left
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {tripSelected && (
        <div className="flex items-center justify-between gap-6 self-stretch mt-6 md:hidden">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-semibold leading-normal text-neutral-800">
              {formatPrice(room.price, { currencySymbol: currency })}
            </span>
            <span className="text-sm font-normal text-neutral-500">
              per guest/trip
            </span>
          </div>
          <div className="flex flex-col justify-center items-end gap-2">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="flex p-1 items-center gap-2 rounded-full"
                onClick={() =>
                  handleGuestCount(
                    guestCount - 1,
                    room.typeId,
                    room.occupancy ?? 1,
                  )
                }
              >
                <IconMinus size={20} />
              </button>
              <span className="text-center text-sm font-semibold">
                {guestCount} guest
              </span>
              <button
                type="button"
                className="flex p-1 items-center gap-2 rounded-full"
                onClick={() =>
                  handleGuestCount(
                    guestCount + 1,
                    room.typeId,
                    room.occupancy ?? 1,
                  )
                }
              >
                <IconPlus size={20} />
              </button>
            </div>
            <div className="flex px-3 py-1 justify-center items-start gap-2 self-stretch rounded-full bg-red-100">
              <span className="text-xs font-semibold text-status-error">
                {room.totalAvailable * (room.occupancy ?? 1)} spaces left
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoomCard;
